// GettingStartedPersonRow — one person the onboarding research found on the
// company's own team page (POB S9, pages/gettingStartedTeam.js): the invite
// switch, the name and job title, the e-mail field the OWNER types into
// (never prefilled) and "Remove".
//
// The row state is the found person (functions/gettingStartedTeam.js
// gettingStartedTeamRows — `person*` fields, so no field falls back to a root
// key). The owner's draft is a separate root key read per prop through
// `gettingStartedDraft`: a typed letter re-runs this row's props and never
// rebuilds the list, so the field keeps its caret.
//
// The switch is the consent: it stays OFF until the owner turns it on, and it
// is disabled until the field holds a valid address (typing never flips it).
// An invited person keeps the row with "Invited" in place of the field.
// Phone and a narrow pane: one card per person; the switch and the field
// stack.
export const GettingStartedPersonRow = {
  flow: 'x',
  align: 'center',
  gap: 'A',
  padding: 'Z A',
  '@mobileL': { flow: 'y', align: 'stretch', gap: 'Z' },
  '@appMobile': { flow: 'y', align: 'stretch', gap: 'Z' },

  GsprHead: {
    flow: 'x',
    align: 'center',
    gap: 'A',
    flex: '1 1 0',
    minWidth: '0',

    GsprSwitch: {
      extends: 'SwitchInput',
      onCheck: (el, s) => el.call('gettingStartedDraft', s.root, s.personId).on,
      // Disabled until the owner typed a valid address; an invited person
      // keeps the box (no shift) with the switch out of reach.
      disabled: (el, s) => {
        const draft = el.call('gettingStartedDraft', s.root, s.personId)
        return s.invitedAt || !el.call('gettingStartedEmailOk', draft.email) ? true : undefined
      },
      visibility: (el, s) => (s.invitedAt ? 'hidden' : 'visible'),
      'aria-label': (el, s) => {
        void s.root?.lang
        return el
          .call('polyglot', 'workspace.gettingStarted.team.switchAria')
          .replace('{name}', s.personName)
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        el.call('toggleGettingStartedInvite', s.root, s.personId)
      }
    },

    GsprWho: {
      flow: 'y',
      gap: 'X',
      minWidth: '0',
      GsprName: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '500',
        color: 'title',
        overflowWrap: 'anywhere',
        text: (el, s) => s.personName
      },
      GsprRole: {
        tag: 'span',
        fontSize: 'Z',
        color: 'caption',
        overflowWrap: 'anywhere',
        hide: (el, s) => !s.personRole,
        text: (el, s) => s.personRole
      }
    }
  },

  GsprEmail: {
    flow: 'y',
    gap: 'X',
    flex: '0 1 G',
    minWidth: '0',
    '@mobileL': { flex: 'none', width: '100%' },
    '@appMobile': { flex: 'none', width: '100%' },

    AppInputCompact: {
      type: 'email',
      inputmode: 'email',
      autocomplete: 'off',
      spellcheck: 'false',
      // Typed addresses are a draft the page keeps, not unsaved work to
      // guard: a pane close keeps them, so no "Discard changes?".
      dirty: false,
      hide: (el, s) => !!s.invitedAt,
      value: (el, s) => el.call('gettingStartedDraft', s.root, s.personId).email,
      placeholder: '{{ workspace.gettingStarted.team.emailPlaceholder | polyglot }}',
      ariaLabel: (el, s) => {
        void s.root?.lang
        return el
          .call('polyglot', 'workspace.gettingStarted.team.emailAria')
          .replace('{name}', s.personName)
      },
      onInput: (e, el, s) => el.call('setGettingStartedEmail', s.root, s.personId, el.node.value)
    },

    GsprInvited: {
      tag: 'span',
      flow: 'x',
      align: 'center',
      gap: 'X',
      minHeight: 'controlHCompact',
      fontSize: 'Z',
      fontWeight: '500',
      color: 'title',
      hide: (el, s) => !s.invitedAt,
      Icon: { name: 'check', boxSize: 'A', color: 'title' },
      Text: { text: '{{ workspace.gettingStarted.team.invited | polyglot }}' }
    },

    // One reserved line under the field (Rule 66): the company tick, or why
    // this row did not go out. A live region from mount, so a change is read.
    GsprNote: {
      tag: 'span',
      role: 'status',
      minHeight: 'Z1',
      fontSize: 'Y1',
      fontWeight: '500',
      color: (el, s) =>
        el.call('gettingStartedDraft', s.root, s.personId).status === 'failed'
          ? 'dangerInk'
          : 'caption',
      text: (el, s) => {
        void s.root?.lang
        const draft = el.call('gettingStartedDraft', s.root, s.personId)
        if (draft.status === 'failed') {
          return el.call(
            'polyglot',
            draft.error === 'invalid_email'
              ? 'workspace.gettingStarted.team.invalidEmail'
              : draft.error === 'removeFailed'
                ? 'workspace.gettingStarted.team.removeFailed'
                : 'workspace.gettingStarted.team.failed'
          )
        }
        if (s.invitedAt) return ''
        return el.call('gettingStartedOnDomain', s.root, draft.email)
          ? el.call('polyglot', 'workspace.gettingStarted.team.company')
          : ''
      }
    }
  },

  GsprRemove: {
    extends: 'AppCapsuleBtnGhost',
    compact: true,
    flexShrink: '0',
    '@mobileL': { alignSelf: 'flex-start' },
    '@appMobile': { alignSelf: 'flex-start' },
    disabled: (el, s) =>
      el.call('gettingStartedDraft', s.root, s.personId).status === 'removing' ? true : undefined,
    'aria-label': (el, s) => {
      void s.root?.lang
      return el
        .call('polyglot', 'workspace.gettingStarted.team.removeAria')
        .replace('{name}', s.personName)
    },
    text: '{{ workspace.gettingStarted.team.remove | polyglot }}',
    onClick: (e, el, s) => {
      // markPageSaved: opt-out — a removal writes at once; the row's e-mail field is a draft (dirty: false), no kit field holds an edit
      e.preventDefault()
      // markPageSaved: opt-out — the server drops a researched row; no kit field holds an edit (the e-mail drafts are dirty: false)
      // A plain id, read before the await: the row state re-binds when the
      // list shrinks.
      const personId = s.personId
      el.call('removeGettingStartedPerson', s.root, personId)
    }
  }
}
